<!--
  这一条的排布顺序：**参数 → 端口 → 状态**，两块条件按钮各自贴着自己的那一块
  （`重新连接` 跟着参数走，`断开连接` 跟着端口走），状态 tag 收在最右。
  这是用户定的口径，不是随手排的。
-->
<div class="uart-bar">
  <button
    nz-button
    nz-popover
    [nzPopoverContent]="paramPanel"
    nzPopoverTrigger="click"
    nzPopoverPlacement="bottomRight"
    (nzPopoverVisibleChange)="paramOpen.set($event)"
    [attr.aria-expanded]="paramOpen()"
  >
    <nz-icon nzType="setting" />
    <span class="uart-options" [class.uart-options-warn]="uart.needsReconnect()">{{ summary() }}</span>
  </button>

  <!--
    只在「参数改了但没生效」时出现，所以不常驻占地方 —— 表头那一行本来就紧。
    配置页原来用一整条 `nz-alert` 解释「串口不支持在打开状态下改参数」，表头里放不下宽条，
    那句话挂到这颗按钮的 tooltip 上：解释还在，只是换了个地方。

    紧跟在参数按钮后面，不是随手放的：它出现的**唯一**原因就是上面那颗按钮里的参数改了，
    两颗挨着，用户不用在整条里找「刚刚那个改动该按哪里生效」。
  -->
  @if (uart.needsReconnect()) {
    <button
      nz-button
      nzType="primary"
      (click)="reconnect()"
      nz-tooltip
      [nzTooltipTitle]="'串口不支持在打开状态下改参数，点这里按新参数重开一次。' | translate"
    >
      {{ '重新连接' | translate }}
    </button>
  }

  <!--
    端口与连接状态**常显**（用户定的口径），六项参数收进左边那颗按钮的浮层里。
    `aria-expanded` 跟着浮层开合走。**不写 `aria-haspopup`**：浮层既不是 dialog 也不是 menu，
    给它挂一个错误的 role 反而会把读屏用户引到错的地方去，`aria-expanded` 已经够表达「这里有个开合」。
  -->
  <button nz-button nz-popover [nzPopoverContent]="portPanel" nzPopoverTrigger="click" nzPopoverPlacement="bottomRight" [nzPopoverVisible]="portOpen()" (nzPopoverVisibleChange)="onPortOpenChange($event)" [attr.aria-expanded]="portOpen()" [disabled]="unsupported()">
    <nz-icon nzType="usb" />
    <span class="uart-port-label">{{ portLabel() }}</span>
    <nz-icon nzType="down" />
  </button>

  @if (uart.connected()) {
    <button nz-button nzDanger (click)="disconnect()">
      <nz-icon nzType="disconnect" />
      {{ '断开连接' | translate }}
    </button>
  }

  <!--
    状态 tag 排在最右：它是**只读的状态**，不是动作。前面那几颗都是要点的，
    把唯一的读数放在末尾，扫一眼时「动作在左、结果在右」不会串。
  -->
  <nz-tag [nzColor]="statusColor()">{{ statusText() }}</nz-tag>
</div>

<!--
  两个浮层的模板放顶层，与配置页那条口径一致：模板引用变量的作用域是整个模板，
  放顶层一眼能看全这个组件到底有哪些可复用片段。

  浮层内容是**本组件的模板**，实例化出来带着本组件的 `_ngcontent`，所以组件样式表里那些
  普通的元素 / 类规则（`.uart-panel`、`nz-input-number` 一类）照样命中 ——
  `_ngcontent` 是实例化时按声明它的模板盖上去的，跟这个节点最后落在 DOM 的哪个位置无关。
  不必因为「浮层挂在 body 底下」就把整张样式表搬去全局。
  真正够不着的是 ng-zorro 内部模板里的那些节点（`.ant-select-dropdown` 一类），
  以及浮层自己的外壳 `.ant-popover` —— 那些归 `src/styles.less` 管。
-->

<ng-template #portPanel>
  <div class="uart-panel">
    @if (portItems().length === 0) {
      <!--
        空态**不用 `nz-empty`**：那套模板的字色是 antd 写死的深灰，压在深色浮层上等于看不见。
        配置页那段注释踩过同一个坑（记录区那边也一样，见 `debug.component.html`）。
      -->
      <p class="uart-panel-empty">{{ '还没有已授权的串口' | translate }}</p>
    } @else {
      <ul class="uart-port-list">
        <!-- track 用端口对象本身：`portItems` 每次重算都会造一批新行对象，按对象引用 track 会整列重建 -->
        @for (item of portItems(); track item.port) {
          <li class="uart-port-item" [class.uart-port-item-active]="item.connected">
            <span class="uart-port-name">{{ item.label }}</span>

            @if (item.lastUsed) {
              <nz-tag nzColor="blue">{{ '上次使用' | translate }}</nz-tag>
            }
            @if (item.connected) {
              <nz-tag nzColor="success">{{ '已连接' | translate }}</nz-tag>
            }

            <span class="uart-port-actions">
              @if (item.connected) {
                <button nz-button (click)="disconnect()">
                  <nz-icon nzType="disconnect" />
                  {{ '断开' | translate }}
                </button>
              } @else {
                <button nz-button nzType="primary" (click)="connect(item.port)">
                  <nz-icon nzType="link" />
                  {{ '连接' | translate }}
                </button>
              }
              <!-- 打开着的端口不能「忘记」：撤销授权前得先松开设备，否则等于把句柄弄丢了 -->
              <button nz-button (click)="forget(item.port)" [disabled]="item.connected">
                {{ '忘记' | translate }}
              </button>
            </span>
          </li>
        }
      </ul>
    }

    <div class="uart-panel-foot">
      <button nz-button nzType="primary" (click)="choosePort()" [disabled]="unsupported()">
        <nz-icon nzType="plus" />
        {{ '选择串口' | translate }}
      </button>
      <button nz-button (click)="refresh()" [disabled]="unsupported()">
        <nz-icon nzType="sync" />
        {{ '刷新' | translate }}
      </button>
    </div>
  </div>
</ng-template>

<ng-template #paramPanel>
  <div class="uart-panel">
    <!--
      用 div 而不是 form 承载 `nz-form`：`nz-form` 的选择器是 `[nz-form]`，任何元素都行，
      而换成真正的 <form> 会让 FormsModule 的 NgForm 接管，届时每个 `[ngModel]`
      都必须带 name 或 standalone —— 这里是改一下就存一下的，没有「提交」这个动作，
      挂一个永远不会 submit 的 form 只是形式主义。label 的 for/id 关联不受影响。
    -->
    <div nz-form nzLayout="vertical" class="uart-param-grid">
      <nz-form-item>
        <nz-form-label nzFor="uart-baud-rate">{{ '波特率' | translate }}</nz-form-label>
        <nz-form-control>
          @if (customBaud()) {
            <div class="uart-inline-field">
              <nz-input-number
                nzId="uart-baud-rate"
                [ngModel]="options().baudRate"
                (ngModelChange)="setBaudRate($event)"
                [nzMin]="1"
                [nzStep]="1"
                [nzDisabled]="unsupported()"
              />
              <button nz-button nzType="dashed" (click)="usePresetBaud()">
                {{ '用预设档位' | translate }}
              </button>
            </div>
          } @else {
            <div class="uart-inline-field">
              <nz-select
                nzId="uart-baud-rate"
                [ngModel]="options().baudRate"
                (ngModelChange)="setBaudRate($event)"
                [nzDisabled]="unsupported()"
              >
                @for (rate of baudRatePresets; track rate) {
                  <nz-option [nzValue]="rate" [nzLabel]="rate + ''" />
                }
              </nz-select>
              <button nz-button nzType="dashed" (click)="customBaud.set(true)">
                {{ '自定义' | translate }}
              </button>
            </div>
          }
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label nzFor="uart-data-bits">{{ '数据位' | translate }}</nz-form-label>
        <nz-form-control>
          <nz-select
            nzId="uart-data-bits"
            [ngModel]="options().dataBits"
            (ngModelChange)="patch({ dataBits: $event })"
            [nzDisabled]="unsupported()"
          >
            @for (bits of dataBitsOptions; track bits) {
              <nz-option [nzValue]="bits" [nzLabel]="bits + ''" />
            }
          </nz-select>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label nzFor="uart-stop-bits">{{ '停止位' | translate }}</nz-form-label>
        <nz-form-control>
          <nz-select
            nzId="uart-stop-bits"
            [ngModel]="options().stopBits"
            (ngModelChange)="patch({ stopBits: $event })"
            [nzDisabled]="unsupported()"
          >
            @for (bits of stopBitsOptions; track bits) {
              <nz-option [nzValue]="bits" [nzLabel]="bits + ''" />
            }
          </nz-select>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label nzFor="uart-parity">{{ '校验位' | translate }}</nz-form-label>
        <nz-form-control>
          <nz-select
            nzId="uart-parity"
            [ngModel]="options().parity"
            (ngModelChange)="patch({ parity: $event })"
            [nzDisabled]="unsupported()"
          >
            @for (option of parityOptions; track option.value) {
              <nz-option [nzValue]="option.value" [nzLabel]="option.label | translate" />
            }
          </nz-select>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label nzFor="uart-flow-control">{{ '流控' | translate }}</nz-form-label>
        <nz-form-control>
          <nz-select
            nzId="uart-flow-control"
            [ngModel]="options().flowControl"
            (ngModelChange)="patch({ flowControl: $event })"
            [nzDisabled]="unsupported()"
          >
            @for (option of flowControlOptions; track option.value) {
              <nz-option [nzValue]="option.value" [nzLabel]="option.label | translate" />
            }
          </nz-select>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label nzFor="uart-buffer-size">{{ '缓冲区大小' | translate }}</nz-form-label>
        <nz-form-control>
          <nz-input-number
            nzId="uart-buffer-size"
            [ngModel]="options().bufferSize"
            (ngModelChange)="setBufferSize($event)"
            [nzMin]="bufferMin"
            [nzMax]="bufferMax"
            [nzStep]="16"
            [nzDisabled]="unsupported()"
          />
        </nz-form-control>
      </nz-form-item>
    </div>

    <div class="uart-panel-foot">
      <button nz-button nzDanger="true" nzType="primary" (click)="resetOptions()" [disabled]="unsupported()">
        <nz-icon nzType="rollback" />
        {{ '恢复默认' | translate }}
      </button>
    </div>
  </div>
</ng-template>
